/**
 * RUM phase 1: initial-load milestones on the `performance.timeOrigin` clock,
 * one beacon per page load to `POST /v1/rum`, sampled (see sampling.ts).
 * `installRum(router)` once from `main.tsx`; `recordRumMilestone(...)`
 * wherever a milestone happens. Runs alongside the legacy
 * `PerformanceProvider` until validated.
 * Design: `docs/proposals/real-user-monitoring.md`.
 */
import { accountStore } from '@/services/auth/store';
import { isHydraRoute } from '@/services/router/route-ownership';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { createRumSession } from './session';
import type { RumRouter } from './session';
import { createRumRequestQueue } from './requests';
import type { RumRequestTiming } from './requests';
import { createRumTransitionTracker } from './transitions';
import { sendRumBeacon } from './beacon';
import { getRumContext } from './context';
import { isRumSessionSampled, shouldSampleRumTransition } from './sampling';
import { RumMilestone } from './types';
import type { RumTransitionOwner } from './types';

const session = createRumSession({ send: sendRumBeacon, getContext: getRumContext });

const requestQueue = createRumRequestQueue({
  send: sendRumBeacon,
  getContext: getRumContext,
  getSessionId: () => session.sessionId,
  getInitialLoadSentAt: session.sentAt,
});

/**
 * Who renders the destination path: 'hydra', or the embedded bundle so
 * embedded transitions — whose in-app timings the shell cannot see — stay
 * distinguishable (and filterable) from fully-measured hydra ones. Evaluated
 * at navigation time so flag-gated ownership reads the loaded account.
 */
function classifyTransitionOwner(pathname: string): RumTransitionOwner {
  const account = accountStore.get();
  if (isHydraRoute(pathname, account)) {
    return 'hydra';
  }
  return getAppBundleFromUrl(pathname, account) ?? 'embedded';
}

const transitionTracker = createRumTransitionTracker({
  send: sendRumBeacon,
  getContext: getRumContext,
  getSessionId: () => session.sessionId,
  getOwner: classifyTransitionOwner,
  shouldSample: shouldSampleRumTransition,
  // The embedded-sync path reports only after the load beacon flushed, so an
  // embedded app's boot-time URL rewriting isn't counted as a navigation.
  isInitialLoadComplete: () => session.sentAt() != null,
});

export function installRum(router: RumRouter): void {
  // Unsampled loads never install: no timers, no listeners, no beacon.
  // Milestones still record into the session harmlessly.
  if (!isRumSessionSampled()) {
    return;
  }
  session.install(router);
  requestQueue.install(router);
  transitionTracker.install(router);
}

/**
 * Report an intra-embedded-app navigation. Called by the embedded-history-sync
 * bridge when it syncs a same-bundle URL change without running the router's
 * navigation lifecycle — the only signal those navigations produce.
 * No-op on unsampled page loads.
 */
export function recordRumEmbeddedTransition(toPathname: string): void {
  transitionTracker.recordEmbeddedSync(toPathname);
}

export const recordRumMilestone = session.recordMilestone;

/**
 * Tell the beacon a lazily-loaded page is on its way, so the quiet-period
 * flush waits for the render instead of dropping the slowest loads. Called by
 * `lazyRoute`; see `useRumPageRendered` for the reporting half.
 */
export const expectRumPageRender = session.expectPageRender;

/**
 * The claim half of useRumTransitionReady (its own file — importing the hook
 * from here would be a module cycle): marks the in-flight transition as
 * page-owned, so the beacon waits for the ready report instead of flushing at
 * settle. Returns the claim token markRumTransitionReady requires; null
 * without a pending transition.
 */
export const claimRumTransition = transitionTracker.claim;

/**
 * The report half: the claimed page's data arrived — record `ready` and
 * flush. Inert unless the token still names the pending transition, so a
 * stale page's late data arrival can't end someone else's measurement.
 */
export const markRumTransitionReady = transitionTracker.markReady;

/**
 * Feed one settled API request into the requests beacon. Wired as the
 * `onRequestTiming` hook on every `APIClient` instance (`services/api/*`).
 * No-op on unsampled page loads.
 */
export function recordRumRequest(timing: RumRequestTiming): void {
  requestQueue.record(timing);
}

/** Milestones embedded apps may report through the window bridge. */
const EMBEDDED_APP_MILESTONES: ReadonlySet<RumMilestone> = new Set([RumMilestone.ChildAppInteractive]);

function toEmbeddedMilestone(name: string): RumMilestone | null {
  for (const milestone of EMBEDDED_APP_MILESTONES) {
    if (milestone === name) {
      return milestone;
    }
  }
  return null;
}

/**
 * The `window.__hydra__.rum` surface (wired in utils/inject-globals). Embedded
 * apps call `expectMilestone` early in boot — the beacon then waits for the
 * matching `recordMilestone` (or the hard cap) before flushing — and
 * `recordMilestone` when the moment happens. Names outside the embedded
 * allowlist are ignored; old app builds that never call it change nothing.
 */
export const rumEmbeddedBridge = {
  expectMilestone(name: string): void {
    const milestone = toEmbeddedMilestone(name);
    if (milestone != null) {
      session.expectMilestone(milestone);
    }
  },
  recordMilestone(name: string): void {
    const milestone = toEmbeddedMilestone(name);
    if (milestone != null) {
      session.recordMilestone(milestone);
    }
  },
};

export { RumMilestone } from './types';
export type { RumTransitionClaim } from './transitions';
export type { RumInitialLoadPayload, RumRequestsPayload, RumSentReason } from './types';
export type { RumRequestTiming } from './requests';
